import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface SendIntroEmailDialogProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const SendIntroEmailDialog: React.FC<SendIntroEmailDialogProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onIgnoreConfirmation = () => {
		if (teaser !== null) ignoreTeaser(teaser);
	};

	const { mutate: ignoreTeaser } = useMutation<DataRoomTeaser, unknown, DataRoomTeaser>(
		teaserToIgnore =>
			getResponseData(
				api.patch<DataRoomTeaser>(CerebroApiEndpoints.Teasers.IntroEmail(dataRoomGuid, teaserToIgnore.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, teaserToIgnore) => {
				const message = teaserToIgnore.sendIntroEmail ? 'send intro email.' : 'do not send intro email.';
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar(message, { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem updating property for sending the intro email.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={teaser !== null} onClose={close}>
			<DialogTitle>Confirm Action</DialogTitle>
			<DialogContent>
				{teaser && !teaser.sendIntroEmail && (
					<DialogContentText>
						Are you sure you want to send the intro email for {teaser?.firstName} {teaser?.lastName} at{' '}
						{teaser?.bankName}? By selecting yes the intro email for DirectConnect/Direct Intro will be sent
						on teaser accepted.
					</DialogContentText>
				)}
				{teaser && teaser.sendIntroEmail && (
					<DialogContentText>
						Are you sure you want to not send the intro email for {teaser?.firstName} {teaser?.lastName} at{' '}
						{teaser?.bankName}? By selecting yes the intro email for DirectConnect/Direct Intro will not be
						sent on teaser accepted.
					</DialogContentText>
				)}
				{!teaser && <DialogContentText></DialogContentText>}
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onIgnoreConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
